import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { translateText } from '../services/api';

// Cache for translations to avoid repeated API calls
const translationCache = new Map();

/**
 * Hook to automatically translate text when language changes
 * Uses Google Translate API with caching
 * @param {string} text - Swedish text to translate
 * @param {string} sourceLang - Source language (default: 'sv')
 * @returns {string} - Translated text
 */
export const useAutoTranslate = (text, sourceLang = 'sv') => {
  const { language } = useLanguage();
  const [translatedText, setTranslatedText] = useState(text);
  const prevLangRef = useRef(language);

  useEffect(() => {
    const doTranslate = async () => {
      // If no text, return empty
      if (!text || text.trim() === '') {
        setTranslatedText('');
        return;
      }

      // If same language as source, return original
      if (language === sourceLang) {
        setTranslatedText(text);
        return;
      }

      // Check cache first
      const cacheKey = `${text}_${sourceLang}_${language}`;
      if (translationCache.has(cacheKey)) {
        setTranslatedText(translationCache.get(cacheKey));
        return;
      }

      // Translate via API
      try {
        const result = await translateText(text, language, sourceLang);
        translationCache.set(cacheKey, result.translated);
        setTranslatedText(result.translated);
      } catch (error) {
        console.error('Translation error:', error);
        setTranslatedText(text); // Fallback to original
      }
    };

    doTranslate();
    prevLangRef.current = language;
  }, [text, language, sourceLang]);

  return translatedText;
};

/**
 * Component that renders auto-translated text
 * More convenient than the hook for simple cases
 */
export const AutoTranslate = ({ 
  children, 
  sourceLang = 'sv',
  as: Component = 'span',
  className = '',
  ...props 
}) => {
  const text = typeof children === 'string' ? children : '';
  const translated = useAutoTranslate(text, sourceLang);
  
  return (
    <Component className={className} {...props}>
      {translated}
    </Component>
  );
};

/**
 * Component that wraps t() function output and auto-translates for non-static languages
 * Use this instead of directly using t() in JSX when you want auto-translation fallback
 */
export const AutoT = ({ 
  tKey,
  as: Component = 'span',
  className = '',
  ...props 
}) => {
  const { t, language } = useLanguage();
  const text = t(tKey);
  
  // Only auto-translate if not Swedish or English (which have static translations)
  const needsTranslation = language !== 'sv' && language !== 'en';
  const translated = useAutoTranslate(needsTranslation ? text : '', 'sv');
  
  return (
    <Component className={className} {...props}>
      {needsTranslation && translated ? translated : text}
    </Component>
  );
};

/**
 * Batch translate multiple texts at once for better performance
 * @param {Object} texts - Object with keys and Swedish text values
 * @returns {Object} - Same keys with translated values
 */
export const useBatchTranslate = (texts, sourceLang = 'sv') => {
  const { language } = useLanguage();
  const [translatedTexts, setTranslatedTexts] = useState(texts);

  useEffect(() => {
    const translateAll = async () => {
      // If same language, return originals
      if (language === sourceLang) {
        setTranslatedTexts(texts);
        return;
      }

      const newTranslations = { ...texts };
      const textsToTranslate = [];

      // Check which texts need translation
      for (const [key, text] of Object.entries(texts)) {
        if (!text || text.trim() === '') continue;
        
        const cacheKey = `${text}_${sourceLang}_${language}`;
        if (translationCache.has(cacheKey)) {
          newTranslations[key] = translationCache.get(cacheKey);
        } else {
          textsToTranslate.push({ key, text });
        }
      }

      // Translate remaining texts
      for (const { key, text } of textsToTranslate) {
        try {
          const result = await translateText(text, language, sourceLang);
          const cacheKey = `${text}_${sourceLang}_${language}`;
          translationCache.set(cacheKey, result.translated);
          newTranslations[key] = result.translated;
        } catch (error) {
          console.error(`Translation error for key ${key}:`, error);
          newTranslations[key] = text;
        }
      }

      setTranslatedTexts(newTranslations);
    };

    translateAll();
  }, [texts, language, sourceLang]);

  return translatedTexts;
};

export default useAutoTranslate;
